import {
  compactIri,
  expandIri,
  findContextConflicts,
  lookupTerm,
  type TermDefinition,
} from '../vocabulary/index.js';
import { sortNodes } from './canonical.js';
import { canonicalNodeFromInterop, isInteropNodeList } from './interop-parse.js';

export interface ParseIssue {
  /** Dotted path into the input document, e.g. `@graph[3].performedBy`. */
  readonly path: string;
  readonly message: string;
}

export interface NormalisedDocument {
  readonly nodes: readonly Record<string, unknown>[];
  readonly issues: readonly ParseIssue[];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Unwraps the JSON-LD node-reference and value-object forms to a bare value. */
function unwrapValue(value: unknown): unknown {
  if (!isPlainObject(value)) return value;
  const id = value['@id'];
  if (typeof id === 'string' && Object.keys(value).length === 1) return id;
  const literal = value['@value'];
  if (literal !== undefined && (typeof literal !== 'object' || literal === null)) return literal;
  return value;
}

function normaliseValue(term: TermDefinition, raw: unknown, path: string, issues: ParseIssue[]): unknown {
  if (Array.isArray(raw)) {
    const values = raw.map(unwrapValue);
    if (term.many) return values;
    if (values.length === 1) return values[0];
    issues.push({
      path,
      message: `${term.term} is single-valued but received ${String(values.length)} values`,
    });
    return values[0];
  }
  const value = unwrapValue(raw);
  return term.many ? [value] : value;
}

function normaliseType(raw: unknown, path: string, issues: ParseIssue[]): unknown {
  const value = Array.isArray(raw) ? (raw.length === 1 ? raw[0] : raw) : raw;
  if (Array.isArray(value)) {
    issues.push({ path: `${path}.@type`, message: 'multiple @type values are not supported' });
    return value;
  }
  // A full IRI is accepted on input and compacted, so a file exported by a
  // generic JSON-LD processor still imports.
  return typeof value === 'string' ? compactIri(expandIri(value)) : value;
}

/**
 * Turns any DPSE JSON-LD document into the canonical node shape the zod schemas
 * expect: alias keys (`phase`, not `dpem:phase`), bare id strings instead of
 * `{"@id": …}`, many-valued terms always arrays, nodes in canonical order.
 *
 * Unknown terms are reported rather than dropped. Silently discarding a term on
 * import would lose author data and make round-trip a lie.
 */
export function normaliseDocument(input: unknown): NormalisedDocument {
  const issues: ParseIssue[] = [];

  let rawNodes: unknown;
  let document: Record<string, unknown> | undefined;
  if (Array.isArray(input)) {
    rawNodes = input;
  } else if (isPlainObject(input)) {
    document = input;
    rawNodes = input['@graph'];
  } else {
    return { nodes: [], issues: [{ path: '', message: 'document must be an object or an array' }] };
  }

  if (!Array.isArray(rawNodes)) {
    return { nodes: [], issues: [...issues, { path: '@graph', message: '@graph must be an array' }] };
  }

  /**
   * The interchange dialect is read back node-first, and its `@context` is not checked for
   * conflicts: that context describes the *dialect's* terms — `name` is `schema:name` there and
   * `rdfs:label` here — so checking it would report a conflict for every compliant export the
   * studio itself wrote. The canonical context is checked, as it always was, because a document
   * that uses our words for someone else's meaning is exactly what that check is for.
   */
  const inputNodes = rawNodes as readonly unknown[];
  const interop = isInteropNodeList(inputNodes);
  const sourceNodes = interop
    ? inputNodes.map((node) => (isPlainObject(node) ? canonicalNodeFromInterop(node) : node))
    : inputNodes;

  if (!interop && document !== undefined) {
    for (const conflict of findContextConflicts(document['@context'])) {
      issues.push({
        path: `@context.${conflict.term}`,
        message: `redefines "${conflict.term}" as ${conflict.actualIri}; DPSE requires ${conflict.expectedIri}`,
      });
    }
  }

  const nodes: Record<string, unknown>[] = [];
  for (let i = 0; i < sourceNodes.length; i += 1) {
    const path = `@graph[${String(i)}]`;
    const raw = sourceNodes[i];
    if (!isPlainObject(raw)) {
      issues.push({ path, message: 'node must be an object' });
      continue;
    }
    const node: Record<string, unknown> = {};
    if ('@id' in raw) node['@id'] = raw['@id'];
    if ('@type' in raw) node['@type'] = normaliseType(raw['@type'], path, issues);

    for (const [key, value] of Object.entries(raw)) {
      if (key === '@id' || key === '@type') continue;
      const term = lookupTerm(key);
      if (term === undefined) {
        issues.push({
          path: `${path}.${key}`,
          message: `unknown term "${key}"; it is not part of the DPSE vocabulary`,
        });
        continue;
      }
      if (term.term in node) {
        issues.push({
          path: `${path}.${key}`,
          message: `term "${term.term}" is declared twice under different spellings`,
        });
        continue;
      }
      node[term.term] = normaliseValue(term, value, `${path}.${key}`, issues);
    }
    nodes.push(node);
  }

  return { nodes: sortNodes(nodes), issues };
}
